import { styled } from '@mui/material';
import { Fragment } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CAST_AI } from '@cast/constants';
import {
  Box,
  Divider,
  DrawerProvider,
  Icons,
  Stack,
  tokens,
  Typography,
} from '@cast/design-system';
import { WORKLOAD_VIEWS } from '@cast/features-cluster-inventory-workloads';
import {
  AskCopilotButton,
  useChatbotState,
  useIsOpsPilotEnabled,
} from '@cast/features-organization-shared-opspilot';
import { getUser } from '@cast/infrastructure-auth';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  useIsDarkMode,
  useToggleDarkMode,
} from '@cast/shared-theme-data-access';
import { useOneclickCreateOrganizationDialog } from '@oneclick/hooks/dialogs/useOneclickCreateOrganizationDialog';

import { AbilityGuard, useAbility } from '@cast/shared-ability-data-access';
import { Link as AppLink } from 'components/router';
import { TrialBadge } from 'components/trial/TrialBadge';
import { useDboUrlParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { NotificationsBell } from 'features/notifications';

import { BASE_PATHS, SETTINGS_PATHS } from '@oneclick/constants/navigation';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { HeaderSearchButton } from '@oneclick/features/global-search';
import { useReportingTrialFleetStats } from '@cast/features-cluster-shared';
import {
  doesReportingMonetizationApply,
  ReportingTrialStatusBadge,
  shouldShowReportingTrialStatus,
  useReportingTrial,
} from '@cast/shared-trial-ui';
import { useReportingContactSalesDialog } from 'core/integrations';
import { useIsViewingEnterpriseOrg } from '../_hooks/useIsViewingEnterpriseOrg';
import { useNavCurrentPageLabel } from '../_hooks/useNavCurrentPageLabel';
import { shouldShowClusterSelect } from '../_utils/shouldShowClusterSelect';
import { ClusterSelect } from '../HeaderSelects/ClusterSelect';
import { DboDatabaseSelect } from '../HeaderSelects/DboDatabaseSelect';
import { DboInstanceSelect } from '../HeaderSelects/DboInstanceSelect';
import { HEADER_HEIGHT } from '../layoutConstants';
import { HeaderIconButton } from './HeaderIconButton';
import { HelpMenu } from './HelpMenu';
import { navBreadcrumbGap } from './navTriggerStyles';
import { NodeSelect } from './NodeSelect/NodeSelect';
import { OrganizationMenu } from './OrganizationMenu';
import { ProfileMenu } from './ProfileMenu';
import { WorkloadSelect } from './WorkloadSelect/WorkloadSelect';

const WORKLOAD_LIST_VIEW_SEGMENTS = new Set<string>(
  Object.values(WORKLOAD_VIEWS)
);

const ONBOARDING_PATH = '/onboarding';
const ONBOARDING_BREADCRUMB = `Welcome to ${CAST_AI}`;

const HeaderRoot = styled(Stack)(({ theme }) => ({
  minHeight: `${HEADER_HEIGHT}px`,
  padding: `${tokens.primitives.space['300']} ${tokens.primitives.space['400']}`,
  borderBottom: `1px solid ${tokens.theme.border.structural.neutral}`,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  backgroundColor: tokens.theme.bg.structural.surface,
})) as typeof Stack;

const LogoButton = styled(AppLink)({
  minWidth: '28px',
  minHeight: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  color: tokens.theme.fg.structural.primary,
  textDecoration: 'none',
  flexShrink: 0,

  '& svg': {
    width: '22px',
    height: '24px',
    display: 'block',
    flexShrink: 0,
  },
});

const LogoWordmark = styled(Typography)({
  color: tokens.theme.fg.structural.primary,
  whiteSpace: 'nowrap',
}) as typeof Typography;

const NavBreadcrumbSeparator = styled(Typography)({
  color: tokens.theme.border.structural.neutral,
  fontSize: '12px',
  lineHeight: 1,
  flexShrink: 0,
}) as typeof Typography;

const NavCurrentPage = styled(Typography)({
  color: tokens.theme.fg.structural.secondary,
  whiteSpace: 'nowrap',
}) as typeof Typography;

const NavBreadcrumbLink = styled(AppLink)({
  color: tokens.theme.fg.structural.secondary,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,

  '&:hover': {
    color: tokens.theme.fg.action.primary,
    textDecoration: 'underline',
  },

  '& span': {
    color: 'inherit',
  },
});

const HeaderActions = styled(Box)({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexShrink: 0,
});

const NotificationsBellSlot = () => (
  <AbilityGuard subject="OrganizationNotifications" action="view">
    <DrawerProvider>
      <NotificationsBell />
    </DrawerProvider>
  </AbilityGuard>
);

const LogoMark = () => (
  <svg viewBox="0 0 275 300" fill="none" aria-hidden="true">
    <path
      d="M263.758 72.63L208.538 24.775C189.484 8.26 165.576 0 141.675 0C117.775 0 93.8718 8.2575 74.8077 24.7775L22.1653 70.4C8.07984 82.6075 0 100.318 0 118.988V181.012C0 199.682 8.07985 217.392 22.1678 229.602L74.8102 275.223C93.2435 291.2 116.989 300 141.675 300C166.361 300 190.107 291.203 208.54 275.225L263.76 227.37C273.363 219.045 277.225 205.715 273.598 193.405C271.88 187.577 268.308 182.188 263.267 177.82L248.65 165.152L224.917 185.723L242.46 200.95C243.016 201.432 243.335 202.132 243.335 202.87C243.335 203.607 243.016 204.31 242.458 204.792L188.297 251.73C175.009 263.245 158.339 269.005 141.675 269.005C125.007 269.005 108.344 263.247 95.0535 251.73L42.4086 206.107C35.134 199.803 30.9607 190.652 30.9607 181.01V118.985C30.9607 109.34 35.134 100.192 42.4086 93.8875L95.0535 48.2625C107.91 37.1225 124.466 30.9875 141.675 30.9875C158.884 30.9875 175.443 37.1225 188.297 48.265L242.532 95.2675C243.088 95.75 243.41 96.45 243.41 97.19C243.41 97.93 243.091 98.63 242.532 99.1125L141.675 186.518L99.5359 149.997L141.673 113.478L166.306 134.857L190 114.325L159.844 88.1925C149.486 79.215 133.862 79.215 123.504 88.1925L75.5182 129.78C69.6547 134.86 66.2916 142.227 66.2916 150C66.2916 157.773 69.6547 165.14 75.5158 170.217L123.501 211.805C133.862 220.785 149.486 220.787 159.844 211.805L263.267 122.173C268.308 117.805 271.88 112.415 273.595 106.588C277.22 94.2825 273.358 80.9525 263.758 72.6275V72.63Z"
      fill="currentColor"
    />
  </svg>
);

/** Fleet stats only load while the reporting trial badge is mounted. */
const HeaderReportingTrialBadge = ({
  onViewPlans,
  onContactSales,
}: {
  onViewPlans: () => void;
  onContactSales: () => void;
}) => {
  const fleet = useReportingTrialFleetStats();
  return (
    <ReportingTrialStatusBadge
      fleet={fleet}
      onViewPlans={onViewPlans}
      onContactSales={onContactSales}
    />
  );
};

const HeaderTrialStatus = () => {
  const navigate = useNavigate();
  const { openReportingContactSales } = useReportingContactSalesDialog();
  const trial = useReportingTrial();

  if (doesReportingMonetizationApply(trial)) {
    if (trial.state === 'pre-trial' || trial.state === 'paid') {
      return null;
    }
    if (!shouldShowReportingTrialStatus(trial)) {
      return null;
    }
    return (
      <HeaderReportingTrialBadge
        onViewPlans={() => navigate(SETTINGS_PATHS.platformUsageReportingPlans)}
        onContactSales={() => openReportingContactSales()}
      />
    );
  }

  return <TrialBadge />;
};

const useRouteChecks = () => {
  const { pathname } = useLocation();
  const { clusterId } = useParams<{ clusterId: string }>();
  const { dbServerId, databaseId } = useDboUrlParams();
  const isDboV2Enabled = useAbility().can('view', 'FeatureDboV2');

  const isDboPath =
    pathname.startsWith(PRODUCT_BASE_PATHS.dbo) && isDboV2Enabled;

  return {
    isOnboarding:
      pathname === ONBOARDING_PATH ||
      pathname.startsWith(`${ONBOARDING_PATH}/`),
    isNodeDetailsRoute: Boolean(
      clusterId && pathname.match(/\/inventory\/nodes\/[^/]+/)
    ),
    isWorkloadDetailsRoute: (() => {
      if (!clusterId) {
        return false;
      }
      const match = pathname.match(/\/inventory\/workloads\/([^/]+)/);
      const segment = match?.[1];
      return Boolean(segment && !WORKLOAD_LIST_VIEW_SEGMENTS.has(segment));
    })(),
    dbo: {
      isDboPath,
      isDboServerSelected: isDboPath && dbServerId,
      isDboDatabaseSelected: isDboPath && dbServerId && databaseId,
    },
  };
};

export const Header = () => {
  const { pathname } = useLocation();

  const { open } = useChatbotState();
  const { opsPilotStatus, onTryOpsPilot, tryOpsPilotDisabledTooltip } =
    useIsOpsPilotEnabled();
  const profile = getUser();
  const { openModal: openCreateOrganizationModal } =
    useOneclickCreateOrganizationDialog();
  const { organizations, currentOrganization, setCurrentOrganization } =
    useOrganizations();
  const isViewingEnterpriseOrg = useIsViewingEnterpriseOrg();
  const currentPageLabel = useNavCurrentPageLabel();
  const isDarkMode = useIsDarkMode();
  const handleToggleDarkMode = useToggleDarkMode();

  const {
    dbo: { isDboDatabaseSelected, isDboServerSelected },
    isNodeDetailsRoute,
    isWorkloadDetailsRoute,
    isOnboarding,
  } = useRouteChecks();

  const showClusterSelect = shouldShowClusterSelect(pathname);

  const handleOpenChatbot = () => {
    open(currentOrganization?.id ?? 'default');
  };

  return (
    <HeaderRoot
      component="header"
      direction="row"
      alignItems="center"
      gap={tokens.primitives.space['400']}
      data-testid="app-header"
    >
      <LogoButton to={BASE_PATHS.clusters} data-testid="app-logo-mark">
        <LogoMark />
        {isOnboarding ? (
          <LogoWordmark variant="P14M" component="span">
            {CAST_AI}
          </LogoWordmark>
        ) : null}
      </LogoButton>

      {isOnboarding ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={navBreadcrumbGap}
          minWidth={0}
        >
          <NavBreadcrumbSeparator component="span">/</NavBreadcrumbSeparator>
          <NavCurrentPage variant="P14M" component="span">
            {ONBOARDING_BREADCRUMB}
          </NavCurrentPage>
        </Stack>
      ) : (
        <>
          <Divider orientation="vertical" flexItem />

          <Stack
            direction="row"
            alignItems="center"
            gap={navBreadcrumbGap}
            minWidth={0}
          >
            <OrganizationMenu
              activeOrganizationId={currentOrganization?.id}
              organizations={organizations}
              onChangeOrganization={(org) => setCurrentOrganization(org.id)}
              onCreateNewOrganization={() => openCreateOrganizationModal()}
            />

            {!isViewingEnterpriseOrg ? (
              isDboServerSelected ? (
                <>
                  <NavBreadcrumbSeparator component="span">
                    /
                  </NavBreadcrumbSeparator>
                  <DboInstanceSelect />
                  {isDboDatabaseSelected ? (
                    <>
                      <NavBreadcrumbSeparator component="span">
                        /
                      </NavBreadcrumbSeparator>
                      <DboDatabaseSelect />
                    </>
                  ) : null}
                </>
              ) : showClusterSelect ? (
                <>
                  <NavBreadcrumbSeparator component="span">
                    /
                  </NavBreadcrumbSeparator>
                  <ClusterSelect />
                </>
              ) : null
            ) : null}

            {(currentPageLabel ?? []).map((item) => (
              <Fragment key={`${item.label}:${item.to ?? ''}`}>
                <NavBreadcrumbSeparator component="span">
                  /
                </NavBreadcrumbSeparator>
                {item.to ? (
                  <NavBreadcrumbLink
                    to={item.to}
                    data-testid={`header-crumb-${item.label}`}
                  >
                    <Typography variant="P14M" component="span">
                      {item.label}
                    </Typography>
                  </NavBreadcrumbLink>
                ) : (
                  <NavCurrentPage variant="P14M" component="span">
                    {item.label}
                  </NavCurrentPage>
                )}
              </Fragment>
            ))}

            {isNodeDetailsRoute ? (
              <>
                <NavBreadcrumbSeparator component="span">
                  /
                </NavBreadcrumbSeparator>
                <NodeSelect />
              </>
            ) : null}

            {isWorkloadDetailsRoute ? (
              <>
                <NavBreadcrumbSeparator component="span">
                  /
                </NavBreadcrumbSeparator>
                <WorkloadSelect />
              </>
            ) : null}
          </Stack>
        </>
      )}
      {/* TODO Move to separate component!  */}
      <HeaderActions>
        {!isOnboarding ? <HeaderTrialStatus /> : null}

        <HeaderSearchButton />
        {!isOnboarding && opsPilotStatus === 'on' ? (
          <AskCopilotButton onClick={handleOpenChatbot} />
        ) : null}
        {!isOnboarding && opsPilotStatus === 'off' ? (
          <AskCopilotButton
            promptToEnable
            onClick={onTryOpsPilot}
            disabled={!!tryOpsPilotDisabledTooltip}
            tooltip={tryOpsPilotDisabledTooltip}
          />
        ) : null}

        {/* Search hidden until command palette is implemented */}

        <HeaderIconButton
          title="Dark mode"
          aria-label="Toggle dark mode"
          data-testid="header-theme-toggle"
          onClick={handleToggleDarkMode}
        >
          {isDarkMode ? (
            <Icons.Sun size={20} color={tokens.theme.fg.structural.secondary} />
          ) : (
            <Icons.Moon
              size={20}
              color={tokens.theme.fg.structural.secondary}
            />
          )}
        </HeaderIconButton>

        {!isOnboarding ? (
          <>
            <HelpMenu />

            <NotificationsBellSlot />

            <ProfileMenu
              user={profile?.firstName || ''}
              email={profile?.email || ''}
            />
          </>
        ) : null}
      </HeaderActions>
    </HeaderRoot>
  );
};
